import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { weightsPresetsKeys } from "@/application/query-keys/weights-presets.keys";
import {
  weightsPresetsRepository,
  type CreateWeightsPresetInput,
  type UpdateWeightsPresetPatch,
} from "@/infrastructure/repositories/weights-presets.supabase.repository";
import type { WeightsPresetRow } from "@/infrastructure/supabase/database.types";

export function useWeightsPresets() {
  return useQuery({
    queryKey: weightsPresetsKeys.list(),
    queryFn: () => weightsPresetsRepository.list(),
  });
}

export function useDefaultWeightsPreset() {
  return useQuery({
    queryKey: weightsPresetsKeys.default(),
    queryFn: () => weightsPresetsRepository.getDefault(),
  });
}

/** Invalidate both the list and the active-preset query so scoring recomputes. */
function useInvalidatePresets() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: weightsPresetsKeys.all });
}

export function useUpdateWeightsPreset() {
  const invalidate = useInvalidatePresets();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateWeightsPresetPatch }) =>
      weightsPresetsRepository.update(id, patch),
    onSettled: invalidate,
  });
}

export function useCreateWeightsPreset() {
  const invalidate = useInvalidatePresets();
  return useMutation({
    mutationFn: (input: CreateWeightsPresetInput) => weightsPresetsRepository.create(input),
    onSettled: invalidate,
  });
}

export function useDuplicateWeightsPreset() {
  const invalidate = useInvalidatePresets();
  return useMutation({
    mutationFn: (id: string) => weightsPresetsRepository.duplicate(id),
    onSettled: invalidate,
  });
}

export function useDeleteWeightsPreset() {
  const invalidate = useInvalidatePresets();
  return useMutation({
    mutationFn: (id: string) => weightsPresetsRepository.remove(id),
    onSettled: invalidate,
  });
}

export function useSetDefaultWeightsPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => weightsPresetsRepository.setDefault(id),
    onMutate: async (id) => {
      // Optimistically flip the active flag so the ranking re-orders instantly.
      await qc.cancelQueries({ queryKey: weightsPresetsKeys.all });
      const prevList = qc.getQueryData<WeightsPresetRow[]>(weightsPresetsKeys.list());
      const prevDefault = qc.getQueryData<WeightsPresetRow | null>(weightsPresetsKeys.default());
      if (prevList) {
        const next = prevList.map((p) => ({ ...p, is_default: p.id === id }));
        qc.setQueryData(weightsPresetsKeys.list(), next);
        const newDefault = next.find((p) => p.id === id) ?? null;
        qc.setQueryData(weightsPresetsKeys.default(), newDefault);
      }
      return { prevList, prevDefault };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prevList) qc.setQueryData(weightsPresetsKeys.list(), ctx.prevList);
      if (ctx?.prevDefault !== undefined) {
        qc.setQueryData(weightsPresetsKeys.default(), ctx.prevDefault);
      }
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: weightsPresetsKeys.all }),
  });
}
